"use client";

import {
  TypeHowItWorksTimeline,
  TypeTimelineDate,
  TypeSection,
} from "@contentful/types";
import { customImageLoader } from "@yardzen/next-client-util";
import { H2 } from "@yardzen/ui/components/h2";
import { P } from "@yardzen/ui/components/p";
import { cn } from "@yardzen/ui/lib/utils";
import Image from "next/image";
import * as React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-light-svg-icons";
import { Document } from "@contentful/rich-text-types";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { Badge } from "@yardzen/ui/components/badge";
import ReactMarkdown from "react-markdown";
import { Link } from "libs/components/top-nav/src/YzV2TopNav/ui/Link";

function useFadeInOnScroll<T extends HTMLElement = HTMLElement>() {
  const ref = React.useRef<T | null>(null);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const prefersReduced = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReduced) {
      setVisible(true);
      return;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.unobserve(entry.target);
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" },
    );

    io.observe(node);
    return () => io.disconnect();
  }, []);

  return { ref, visible };
}

const WRAP_SECTION =
  "relative mt-[-100px] w-full bg-gradient-to-b from-lime-50 to-white pt-[120px]";
const WRAP_CONTAINER = "mx-auto px-4 pt-10 sm:px-6 sm:py-12 md:px-8";
const WRAP_TIMELINE = "relative mx-auto max-w-7xl px-2 pb-12 sm:px-4 md:px-8";

const GRID_LIST = " ml-3 pl-10 md:pl-0 grid grid-cols-1";

const GRID_ITEM = "md:grid md:grid-cols-[10vw_56px_1fr] items-start gap-x-3";

const TITLE_PILL =
  "text-brand-primary-regular inline-block rounded-full bg-white px-3 py-1 text-sm font-bold text-nowrap relative z-10 border border-gray-200 mb-2";
const TITLE_DESC = "text-brand-moss-greens-90 my-2 max-w-sm text-xs md:block";

const TIMELINE_COL =
  "relative ml-4 md:ml-8 flex items-start border-l border-dashed border-gray-200 h-full";

const CONNECTOR_BASE = "relative z-10";

// mobile one
const CONNECTOR_LEFT =
  "after:content-[''] after:absolute after:right-full after:top-1/2 after:h-px after:w-8 after:border-t after:border-dashed after:border-gray-200 " +
  "before:absolute before:right-[calc(100%+22px)] before:top-1/2 before:-translate-y-1/2 before:h-3 before:w-3 before:rounded-full before:bg-brand-greens-light before:border before:border-gray-200 before:content-['']";

const CONNECTOR_RIGHT_LG =
  "md:after:content-[''] md:after:absolute md:after:left-full md:after:top-1/2 md:after:h-px md:after:w-9 md:after:border-t md:after:border-dashed md:after:border-gray-200 " +
  "md:before:absolute md:before:left-[calc(100%+46px)] md:before:top-1/2 md:before:-translate-y-1/2 md:before:h-3 md:before:w-3 md:before:rounded-full md:before:bg-brand-greens-light md:before:border md:before:border-gray-200 md:before:content-['']";

const CONNECTOR_WITH_DOT_LEFT =
  "relative after:absolute after:right-full after:top-1/2 after:h-px after:w-8 after:border-t after:border-dashed after:border-gray-200 after:content-[''] " +
  "z-10 before:absolute before:right-[calc(100%+22px)] before:top-1/2 before:-translate-y-1/2 before:h-3 before:w-3 before:rounded-full before:bg-brand-greens-light before:border before:border-gray-200 before:content-['']";

const CARD_WRAPPER =
  "mb-6 flex flex-col md:flex-row items-center gap-x-3 rounded-lg bg-white p-5 shadow-lg border border-brand-primary-regular/10";
const CARD_IMAGE_WRAPPER = "relative flex-1 aspect-[4/3] w-full mb-3 md:mb-0";
const CARD_IMAGE = "object-cover rounded-lg";
const CARD_CONTENT = "flex-1 sm:p-6 md:p-7";
const CARD_TITLE =
  "text-brand-primary-dark text-3xl tracking-tight sm:text-4xl";
const CARD_PROSE =
  "prose prose-neutral prose-p:leading-7 text-brand-moss-greens-90 mt-3 max-w-none";

const MILESTONE_WRAPPER =
  "bg-brand-greens-light/50 inline-flex max-w-full items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-neutral-800";
const MILESTONE_ICON =
  "bg-brand-greens-regular inline-flex h-5 w-5 items-center justify-center rounded-full";

type NestedImageEntry = {
  fields?: {
    image?: {
      fields?: {
        file?: { url?: string };
        description?: string | null;
      };
    };
  };
};

function getSectionImage(
  section: TypeSection<"WITHOUT_UNRESOLVABLE_LINKS">,
): { url: string; alt: string } | null {
  const imgEntry = section.fields.image as unknown as
    | NestedImageEntry
    | undefined;
  const url = imgEntry?.fields?.image?.fields?.file?.url;
  const altCandidate =
    imgEntry?.fields?.image?.fields?.description ?? section.fields.title ?? "";
  if (typeof url === "string" && url.length > 0) {
    return {
      url: url.startsWith("http") ? url : `https:${url}`,
      alt: altCandidate ?? "",
    };
  }
  return null;
}

function getSectionBody(
  section: TypeSection<"WITHOUT_UNRESOLVABLE_LINKS">,
): Document | null {
  const maybeWithJson = section.fields.description as unknown as
    | { json?: Document }
    | undefined;
  if (maybeWithJson?.json) return maybeWithJson.json;
  const maybeDoc = section.fields.description as unknown as
    | Document
    | undefined;
  return maybeDoc ?? null;
}

/* ===============================
   Card
   =============================== */
const Card: React.FC<{
  section: TypeSection<"WITHOUT_UNRESOLVABLE_LINKS">;
}> = ({ section }) => {
  const image = getSectionImage(section);
  const bodyDoc = getSectionBody(section);
  const { ref, visible } = useFadeInOnScroll();

  return (
    <article
      ref={ref}
      className={cn(
        CARD_WRAPPER,
        "translate-y-3 opacity-0 transition-all duration-700 ease-out will-change-transform",
        visible && "translate-y-0 opacity-100",
      )}
      style={{ backgroundColor: section.fields.backgroundColor || "#fff" }}
    >
      {image && (
        <div className={CARD_IMAGE_WRAPPER}>
          <Image
            src={image.url}
            alt={image.alt}
            fill
            className={CARD_IMAGE}
            loader={customImageLoader}
            sizes="(min-width: 1024px) 960px, 100vw"
            priority={false}
          />
        </div>
      )}
      <div className={CARD_CONTENT}>
        {section.fields.badge && (
          <Badge variant="secondary" className="mb-3">
            {section.fields.badge}
          </Badge>
        )}
        <H2 className={CARD_TITLE}>{section.fields.title}</H2>
        <div className={CARD_PROSE}>
          {bodyDoc ? documentToReactComponents(bodyDoc) : null}
        </div>
      </div>
    </article>
  );
};

/* ===============================
   Milestone
   =============================== */
const Milestone: React.FC<{ label: string }> = ({ label }) => {
  const { ref, visible } = useFadeInOnScroll<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={cn(
        MILESTONE_WRAPPER,
        "translate-y-2 opacity-0 transition-all duration-500 ease-out will-change-transform",
        visible && "translate-y-0 opacity-100",
      )}
    >
      <span className={MILESTONE_ICON}>
        <FontAwesomeIcon icon={faCheck} className="h-3.5 w-3.5" />
      </span>
      <span className="pr-1">{label}</span>
    </div>
  );
};

/* ===============================
   HowItWorksTimeline
   =============================== */
type Props = {
  howItWorksTimeline: TypeHowItWorksTimeline<"WITHOUT_UNRESOLVABLE_LINKS">;
  className?: string;
};

export const HowItWorksTimeline: React.FC<Props> = ({
  howItWorksTimeline,
  className,
}) => {
  const { headline, subheading } = howItWorksTimeline.fields;

  const datesFromField = (
    howItWorksTimeline.fields as unknown as {
      dates?: ReadonlyArray<
        TypeTimelineDate<"WITHOUT_UNRESOLVABLE_LINKS">
      > | null;
    }
  ).dates;
  const datesFromCollection = (
    howItWorksTimeline.fields as unknown as {
      datesCollection?: {
        items?: ReadonlyArray<TypeTimelineDate<"WITHOUT_UNRESOLVABLE_LINKS"> | null> | null;
      } | null;
    }
  ).datesCollection?.items?.filter(
    (i): i is TypeTimelineDate<"WITHOUT_UNRESOLVABLE_LINKS"> => Boolean(i),
  );

  const dates: ReadonlyArray<TypeTimelineDate<"WITHOUT_UNRESOLVABLE_LINKS">> =
    datesFromField && Array.isArray(datesFromField) && datesFromField.length > 0
      ? datesFromField
      : (datesFromCollection ?? []);

  return (
    <section className={cn(WRAP_SECTION, className)}>
      <div className={WRAP_CONTAINER}>
        <H2 className="text-brand-primary-darker text-center text-4xl sm:text-4xl md:text-6xl">
          {headline}
        </H2>
        {subheading && (
          <P className="mt-3 max-w-3xl text-base/7 text-neutral-700 sm:text-lg/8">
            <ReactMarkdown
              components={{
                a: ({ href, children }) => (
                  <Link
                    href={href ?? "#"}
                    style={{ textDecoration: "underline" }}
                  >
                    {children}
                  </Link>
                ),
                p: ({ children }) => <span>{children}</span>,
              }}
            >
              {subheading}
            </ReactMarkdown>
          </P>
        )}
      </div>

      <div className={WRAP_TIMELINE}>
        <ol className={GRID_LIST}>
          {dates.map((d, idx) => (
            <React.Fragment key={d.sys?.id ?? idx}>
              <li className={GRID_ITEM}>
                <div className="col-start-2 text-left md:col-start-1 md:text-right">
                  {d.fields.title && (
                    <div
                      className={cn(
                        CONNECTOR_BASE,
                        CONNECTOR_LEFT,
                        CONNECTOR_RIGHT_LG,
                      )}
                    >
                      <P className={TITLE_PILL}>{d.fields.title}</P>
                    </div>
                  )}
                  {d.fields.description && (
                    <P className={TITLE_DESC} noSpacing>
                      {d.fields.description}
                    </P>
                  )}
                </div>

                <div
                  className="h-[calc(100% - 100px)] absolute left-[14px] top-[19px] col-start-1 md:relative md:left-[8px] md:col-start-2 md:h-full
             [li:last-child_&]:h-40 md:[li:last-child_&]:h-40"
                >
                  <div className={TIMELINE_COL} />
                </div>
                <div className="mb-12">
                  {d.fields.section && (
                    <div className={cn("relative", CONNECTOR_WITH_DOT_LEFT)}>
                      <Card
                        section={
                          d.fields
                            .section as TypeSection<"WITHOUT_UNRESOLVABLE_LINKS">
                        }
                      />
                    </div>
                  )}

                  {d.fields.dateSuccess && (
                    <div className={cn("relative", CONNECTOR_WITH_DOT_LEFT)}>
                      <Milestone label={d.fields.dateSuccess} />
                    </div>
                  )}
                </div>
              </li>
            </React.Fragment>
          ))}
        </ol>
      </div>
    </section>
  );
};
